/**
 * FirsatSeridi — Sidebar'a yerleşik kalıcı "Size Özel Fırsat" şeridi
 * v3.0210
 *
 * Davranış:
 *  - /api/panel/size-ozel-firsatlar?limit=5 ile en yüksek tasarruflu ürün(ler)i çeker
 *  - Sidebar açıkken zarif kart, kapalıyken nokta+yüzde mini-rozet
 *  - Her 8 saniyede bir sonraki ürüne döner (carousel)
 *  - X tıklayınca 24 saat boyunca gösterme (localStorage)
 *  - Tıklanınca ürün detayı route'una gider
 *  - Sadece 'client' rolü için (admin/firma için gizli)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, TrendingDown } from 'lucide-react';
import { gorunurAralik } from '../utils/gorunurAralik';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_DISMISS_KEY = 'firsat_serit_dismissed_until_v3';
const REFRESH_MS = 5 * 60 * 1000; // 5 dakika
const ROTATE_MS = 8 * 1000; // 8 saniye

// Müşterinin sol menüsü her sayfa geçişinde yeniden bağlanıyor → şerit her geçişte ve sepetteki her
// +/- tıklamasında yeniden soruyordu (2026-10-23). Son yanıt modül düzeyinde saklanır: 5 dk içinde
// yeniden bağlanınca sunucuya gidilmez; sepet değişince 2 sn bekleyip TEK istek atılır.
let _onbellek = { kim: null, zaman: 0, items: null };

const FirsatSeridi = ({ isOpen, user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hidden, setHidden] = useState(true);

  // Dismiss kontrolü
  useEffect(() => {
    if (!user) return;
    const blockedRoles = ['firma', 'admin', 'superadmin', 'yetsanadmin'];
    if (blockedRoles.includes(user.role)) {
      setHidden(true);
      return;
    }
    try {
      const dismissedUntil = parseInt(localStorage.getItem(STORAGE_DISMISS_KEY) || '0', 10);
      if (dismissedUntil && Date.now() < dismissedUntil) {
        setHidden(true);
        return;
      }
    } catch { /* yoksay */ }
    setHidden(false);
  }, [user]);

  const kim = user?.id || user?.user_id || user?.email || null;
  const fetchItems = useCallback(async (zorla = false) => {
    if (hidden) return;
    if (!zorla && _onbellek.items && _onbellek.kim === kim && Date.now() - _onbellek.zaman < REFRESH_MS) {
      setItems(_onbellek.items);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const r = await axios.get(`${BACKEND_URL}/api/panel/size-ozel-firsatlar?limit=5`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,   // soğuk hesaplama yoğun saatte 8 sn'yi aşabiliyordu → şerit hiç dolmuyordu
      });
      if (r.data?.success && Array.isArray(r.data.items)) {
        // Stoklu olanlara öncelik, sonra tasarrufu yüksek
        const sorted = [...r.data.items].sort((a, b) => {
          if ((a.stoksuz ? 1 : 0) !== (b.stoksuz ? 1 : 0)) return a.stoksuz ? 1 : -1;
          return (b.tasarruf_tl || 0) - (a.tasarruf_tl || 0);
        });
        _onbellek = { kim, zaman: Date.now(), items: sorted };
        setItems(sorted);
      }
    } catch { /* sessizce yoksay */ }
  }, [hidden, kim]);

  useEffect(() => {
    if (hidden) return;
    fetchItems();
    const durdur = gorunurAralik(() => fetchItems(), REFRESH_MS);   // yalnız görünür sekmede
    let bekle = null;
    const onSepet = () => {
      clearTimeout(bekle);
      bekle = setTimeout(() => fetchItems(true), 2000);
    };
    window.addEventListener('sepetGuncellendi', onSepet);
    return () => { durdur(); clearTimeout(bekle); window.removeEventListener('sepetGuncellendi', onSepet); };
  }, [hidden, fetchItems]);

  // Liste değişince activeIdx'i sınırla
  useEffect(() => {
    if (activeIdx >= items.length) setActiveIdx(0);
  }, [items.length, activeIdx]);

  // Carousel rotate
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setActiveIdx(i => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(STORAGE_DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* yoksay */ }
    setHidden(true);
  };

  const handleClick = () => {
    if (items.length === 0) return;
    const item = items[activeIdx] || items[0];
    if (!item?.sku) return;
    // PanelUrunler highlight + modal aç (firsat=true PanelUrunler'i fırsat-only moda
    // alıp genel kataloğu yüklemiyor → SKU bulunamıyor, kullanmıyoruz)
    navigate(`/panel/urunler?highlight=${encodeURIComponent(item.sku)}&modal=1`);
  };

  if (hidden || items.length === 0) return null;
  const item = items[activeIdx] || items[0];
  if (!item) return null;
  const tasarruf = Math.round(item.tasarruf_tl || 0);
  const efektif = Math.round(item.efektif_iskonto_yuzdesi || 0);

  // Sidebar kapalı → kompakt mini-rozet (efektif yüzde)
  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid="firsat-serit-mini"
        className="relative mx-auto my-2 w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white shadow-md flex items-center justify-center transition-all hover:scale-105"
        title={`${item.urun_adi} — %${efektif} indirim, ${tasarruf} ₺ tasarruf`}
      >
        <Sparkles size={14} className="absolute top-1 right-1 text-emerald-200 animate-pulse" />
        <span className="text-[11px] font-bold tabular-nums">%{efektif}</span>
      </button>
    );
  }

  // Sidebar açık → genişletilmiş kart
  return (
    <div
      data-testid="firsat-serit-acik"
      onClick={handleClick}
      className="mx-2 my-2 relative cursor-pointer group rounded-lg overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-600 shadow-md hover:shadow-emerald-500/30 hover:shadow-xl transition-all"
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* X dismiss butonu */}
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="firsat-serit-kapat"
        className="absolute top-1 right-1 z-10 p-0.5 rounded text-emerald-100 hover:text-white hover:bg-emerald-700/50 transition"
        title="24 saat boyunca gizle"
      >
        <X size={11} />
      </button>

      <div className="px-2.5 py-2">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles size={10} className="text-emerald-200 flex-shrink-0" />
          <span className="text-[9px] uppercase tracking-wider text-emerald-100 font-semibold">Size Özel Fırsat</span>
        </div>

        <p className="text-[11px] text-white font-medium leading-tight truncate" title={item.urun_adi}>
          {item.urun_adi}
        </p>
        <p className="text-[9px] text-emerald-100/80 font-mono leading-tight mt-0.5">{item.sku}</p>

        <div className="mt-1.5 flex items-center justify-between gap-1.5">
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/15 rounded text-white text-[10px] font-bold">
            <span>%{efektif}</span>
          </div>
          <div className="inline-flex items-center gap-1 text-white text-[10px]">
            <TrendingDown size={10} />
            <span className="tabular-nums font-semibold">{tasarruf.toLocaleString('tr-TR')} ₺</span>
          </div>
        </div>

        {/* Carousel dots */}
        {items.length > 1 && (
          <div className="mt-1 flex items-center justify-center gap-0.5">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-0.5 rounded-full transition-all duration-300 ${i === activeIdx ? 'w-3 bg-white' : 'w-1 bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FirsatSeridi;
